import { AssignmentDTO, CreateAssignmentRequest } from '@/models';
import { assignmentService } from '@/services/api/assignmentService';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface AssignmentState {
  assignments: AssignmentDTO[];
  isLoading: boolean;
  error: string | null;
  fetchAssignments: (institutionId: string, userId: string) => Promise<void>;
  syncRoleAssignments: (
    institutionId: string,
    userId: string,
    roleId: string,
    desiredAssignments: CreateAssignmentRequest[]
  ) => Promise<void>;
  clearAssignments: () => void;
  clearError: () => void;
}

const assignmentKey = (assignment: Pick<AssignmentDTO, 'scopeType' | 'scopeId'>) =>
  `${assignment.scopeType}:${assignment.scopeId}`;

export const useAssignmentStore = create<AssignmentState>()(
  devtools(
    (set, get) => ({
      assignments: [],
      isLoading: false,
      error: null,

      fetchAssignments: async (institutionId: string, userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const assignments = await assignmentService.getAll(institutionId, userId);
          set({ assignments, isLoading: false });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch user assignments'),
            isLoading: false,
          });
          throw error;
        }
      },

      syncRoleAssignments: async (
        institutionId: string,
        userId: string,
        roleId: string,
        desiredAssignments: CreateAssignmentRequest[]
      ) => {
        set({ isLoading: true, error: null });
        try {
          const currentAssignments = get().assignments.filter(
            (assignment) => assignment.roleId === roleId && assignment.isActive
          );
          const currentKeys = new Set(currentAssignments.map(assignmentKey));
          const desiredKeys = new Set(desiredAssignments.map(assignmentKey));

          for (const assignment of desiredAssignments) {
            if (!currentKeys.has(assignmentKey(assignment))) {
              await assignmentService.create(institutionId, userId, assignment);
            }
          }

          for (const assignment of currentAssignments) {
            if (!desiredKeys.has(assignmentKey(assignment))) {
              await assignmentService.revoke(institutionId, userId, assignment.id);
            }
          }

          const assignments = await assignmentService.getAll(institutionId, userId);
          set({ assignments, isLoading: false });
        } catch (error: unknown) {
          try {
            const assignments = await assignmentService.getAll(institutionId, userId);
            set({ assignments });
          } catch {
            // Keep the synchronization failure as the user-facing error.
          }
          set({
            error: getErrorMessage(error, 'Failed to update user assignments'),
            isLoading: false,
          });
          throw error;
        }
      },

      clearAssignments: () => set({ assignments: [] }),
      clearError: () => set({ error: null }),
    }),
    { name: 'AssignmentStore' }
  )
);
